import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  History, RefreshCw, Filter, Users, ChevronLeft, ChevronRight,
  Plus, Edit2, Trash2, FileText, UtensilsCrossed, Wine, Euro,
  Building2, Carrot, ClipboardCheck, Settings, Loader2
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AKTION_ICONS = {
  erstellt: { icon: Plus, color: "text-emerald-400", bg: "bg-emerald-500/20" },
  bearbeitet: { icon: Edit2, color: "text-blue-400", bg: "bg-blue-500/20" },
  geloescht: { icon: Trash2, color: "text-red-400", bg: "bg-red-500/20" },
};

const BEREICH_ICONS = {
  mitarbeiter: Users,
  speisen: UtensilsCrossed,
  getraenke: Wine,
  einnahmen: Euro,
  fixkosten: Building2,
  zutaten: Carrot,
  checklisten: ClipboardCheck,
  einstellungen: Settings,
  standorte: Building2,
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const AuditTrail = () => {
  const [audit, setAudit] = useState(null);
  const [zusammenfassung, setZusammenfassung] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterBereich, setFilterBereich] = useState("alle");
  const [filterAktion, setFilterAktion] = useState("alle");
  const [page, setPage] = useState(0);
  const LIMIT = 30;

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/audit-trail?limit=${LIMIT}&skip=${page * LIMIT}`;
      if (filterBereich !== "alle") url += `&bereich=${filterBereich}`;
      if (filterAktion !== "alle") url += `&aktion=${filterAktion}`;

      const [auditRes, summRes] = await Promise.all([
        axios.get(url, { withCredentials: true }),
        page === 0 ? axios.get(`${API}/audit-trail/zusammenfassung`, { withCredentials: true }) : Promise.resolve(null)
      ]);
      setAudit(auditRes.data);
      if (summRes) setZusammenfassung(summRes.data);
    } catch (error) {
      console.error("Audit-Trail Fehler:", error);
    } finally {
      setLoading(false);
    }
  }, [page, filterBereich, filterAktion]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  return (
    <div className="space-y-4" data-testid="audit-trail-tab">
      {/* Zusammenfassung */}
      {zusammenfassung && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-400">Gesamt Eintraege</p>
            <p className="text-2xl font-bold text-white">{zusammenfassung.total_gesamt}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-400">Letzte 7 Tage</p>
            <p className="text-2xl font-bold text-violet-400">{zusammenfassung.total_7_tage}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-400">Aktivste Bereiche (7T)</p>
            <div className="mt-1 space-y-0.5">
              {zusammenfassung.nach_bereich?.slice(0, 3).map((b, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-slate-300 capitalize">{b.bereich}</span>
                  <span className="text-white font-mono">{b.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <p className="text-xs text-slate-400">Aktive Benutzer (7T)</p>
            <div className="mt-1 space-y-0.5">
              {zusammenfassung.nach_benutzer?.slice(0, 3).map((b, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-slate-300">{b.name}</span>
                  <span className="text-white font-mono">{b.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-400" />
          <Select value={filterBereich} onValueChange={(v) => { setFilterBereich(v); setPage(0); }}>
            <SelectTrigger className="w-[160px] bg-slate-700 border-slate-600 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Bereiche</SelectItem>
              <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
              <SelectItem value="speisen">Speisen</SelectItem>
              <SelectItem value="getraenke">Getraenke</SelectItem>
              <SelectItem value="einnahmen">Einnahmen</SelectItem>
              <SelectItem value="fixkosten">Fixkosten</SelectItem>
              <SelectItem value="zutaten">Zutaten</SelectItem>
              <SelectItem value="checklisten">Checklisten</SelectItem>
              <SelectItem value="einstellungen">Einstellungen</SelectItem>
              <SelectItem value="standorte">Standorte</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAktion} onValueChange={(v) => { setFilterAktion(v); setPage(0); }}>
            <SelectTrigger className="w-[140px] bg-slate-700 border-slate-600 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle Aktionen</SelectItem>
              <SelectItem value="erstellt">Erstellt</SelectItem>
              <SelectItem value="bearbeitet">Bearbeitet</SelectItem>
              <SelectItem value="geloescht">Geloescht</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" onClick={fetchAudit} className="border-slate-600 h-8">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </Button>
        <span className="text-xs text-slate-500 ml-auto">{audit?.total || 0} Eintraege</span>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </div>
      ) : audit?.eintraege?.length > 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <div className="divide-y divide-slate-700/50">
            {audit.eintraege.map((e) => {
              const aktionInfo = AKTION_ICONS[e.aktion] || AKTION_ICONS.bearbeitet;
              const AktionIcon = aktionInfo.icon;
              const BereichIcon = BEREICH_ICONS[e.bereich] || FileText;
              return (
                <div key={e.id} className="flex items-start gap-3 p-3 hover:bg-slate-700/30 transition-colors">
                  <div className={`p-1.5 rounded-lg ${aktionInfo.bg} flex-shrink-0 mt-0.5`}>
                    <AktionIcon size={14} className={aktionInfo.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white">{e.user_name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${aktionInfo.bg} ${aktionInfo.color}`}>{e.aktion}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-slate-700 rounded text-slate-300 flex items-center gap-1">
                        <BereichIcon size={10} /> {e.bereich}
                      </span>
                      {e.rolle && e.rolle !== "admin" && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-violet-900/30 rounded text-violet-400">{e.rolle}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{e.details}</p>
                  </div>
                  <span className="text-[10px] text-slate-500 flex-shrink-0 whitespace-nowrap">{formatDateTime(e.zeitpunkt)}</span>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {audit.total > LIMIT && (
            <div className="flex items-center justify-between p-3 border-t border-slate-700">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="border-slate-600 gap-1">
                <ChevronLeft size={14} /> Zurueck
              </Button>
              <span className="text-xs text-slate-400">
                Seite {page + 1} von {Math.ceil(audit.total / LIMIT)}
              </span>
              <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= audit.total} onClick={() => setPage(p => p + 1)} className="border-slate-600 gap-1">
                Weiter <ChevronRight size={14} />
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 bg-slate-800/30 rounded-xl border border-slate-700">
          <History size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-white font-semibold">Noch keine Aktivitaeten</h3>
          <p className="text-slate-400 text-sm mt-1">Sobald Mitarbeiter oder Sie Aenderungen vornehmen, erscheinen diese hier.</p>
        </div>
      )}
    </div>
  );
};

export default AuditTrail;
